import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemDecorator,
  ListItemContent,
  LinearProgress,
  Stack,
} from '@mui/joy';
import {
  Rocket as DeployIcon,
  ClockCounterClockwise as HistoryIcon,
  Eye as PreviewIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  XCircle as ErrorIcon,
  ArrowsClockwise as RefreshIcon,
  Gear as SettingsIcon,
  ArrowSquareOut as LaunchIcon,
} from '@phosphor-icons/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface DeploymentStatus {
  id: string;
  environment: 'staging' | 'production';
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'rolled_back';
  created_at: string;
  completed_at?: string;
  deployed_by: string;
  version: string;
  url?: string;
}

interface PreflightCheck {
  id: string;
  name: string;
  description: string;
  status: 'pass' | 'fail' | 'warning' | 'checking';
  message?: string;
  fixable?: boolean;
}

interface SiteDeploymentData {
  site: {
    id: number;
    name: string;
    domain: string;
    status: 'draft' | 'published' | 'deployed';
  };
  current_deployments: {
    staging?: DeploymentStatus;
    production?: DeploymentStatus;
  };
  last_deployment?: DeploymentStatus;
  preflight_checks: PreflightCheck[];
  can_deploy: boolean;
  deployment_url?: string;
  staging_url?: string;
}

export const DeploymentDashboard: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedEnvironment, setSelectedEnvironment] = useState<'staging' | 'production'>('staging');

  // Fetch deployment data
  const { data: deploymentData, isLoading, refetch } = useQuery<SiteDeploymentData>({
    queryKey: ['site-deployment', siteId],
    queryFn: async (): Promise<SiteDeploymentData> => {
      const response = await api.get(`/api/cms/sites/${siteId}/deployment/`);
      return response.data as SiteDeploymentData;
    },
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  // Deploy mutation
  const deployMutation = useMutation({
    mutationFn: async (environment: 'staging' | 'production') => {
      const response = await api.post(`/api/cms/sites/${siteId}/deploy/`, {
        environment,
        validate: true,
        auto_rollback: true,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-deployment', siteId] });
    },
  });

  // Rollback mutation
  const rollbackMutation = useMutation({
    mutationFn: async (deploymentId: string) => {
      const response = await api.post(`/api/cms/deployments/${deploymentId}/rollback/`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-deployment', siteId] });
    },
  });

  const handleDeploy = async (environment: 'staging' | 'production') => {
    if (!deploymentData?.can_deploy) {
      navigate(`/cms/sites/${siteId}/deploy/wizard`);
      return;
    }
    
    await deployMutation.mutateAsync(environment);
  };

  const handleRollback = async (deployment: DeploymentStatus) => {
    if (window.confirm('Are you sure you want to rollback this deployment?')) {
      await rollbackMutation.mutateAsync(deployment.id);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'in_progress': return 'warning';
      case 'rolled_back': return 'neutral';
      default: return 'neutral';
    }
  };

  const getCheckIcon = (status: string) => {
    switch (status) {
      case 'pass': return <CheckIcon color="var(--joy-palette-success-500)" />;
      case 'fail': return <ErrorIcon color="var(--joy-palette-danger-500)" />;
      case 'warning': return <WarningIcon color="var(--joy-palette-warning-500)" />;
      default: return <RefreshIcon />;
    }
  };

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography level="h2">Deployment Dashboard</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!deploymentData) {
    return (
      <Box p={3}>
        <Alert color="danger">Failed to load deployment data</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography level="h2">Deployment Dashboard</Typography>
          <Typography level="body-lg" color="neutral">
            {deploymentData.site?.name || 'Site'} • {deploymentData.site?.domain || 'example.com'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<HistoryIcon />}
            onClick={() => navigate(`/cms/sites/${siteId}/deploy/history`)}
          >
            History
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<SettingsIcon />}
            onClick={() => navigate(`/cms/sites/${siteId}/settings`)}
          >
            Settings
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Current Deployments */}
        <Grid xs={12} md={8}>
          <Stack spacing={3}>
            {/* Staging Environment */}
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography level="h4">Staging Environment</Typography>
                  {deploymentData.current_deployments?.staging && (
                    <Chip color={getStatusColor(deploymentData.current_deployments.staging.status)}>
                      {deploymentData.current_deployments.staging.status}
                    </Chip>
                  )}
                </Box>
                
                {deploymentData.current_deployments?.staging ? (
                  <Box>
                    <Typography level="body-sm" mb={1}>
                      Version: {deploymentData.current_deployments.staging.version}
                    </Typography>
                    <Typography level="body-sm" mb={2}>
                      Deployed: {new Date(deploymentData.current_deployments.staging.created_at).toLocaleString()}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {deploymentData.staging_url && (
                        <Button
                          size="sm"
                          variant="outlined"
                          color="primary"
                          startDecorator={<PreviewIcon />}
                          onClick={() => window.open(deploymentData.staging_url, '_blank')}
                        >
                          Preview
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outlined"
                        color="warning"
                        onClick={() => handleRollback(deploymentData.current_deployments!.staging!)}
                      >
                        Rollback
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Box textAlign="center" py={3}>
                    <Typography level="body-sm" color="neutral" mb={2}>
                      No staging deployment
                    </Typography>
                    <Button
                      variant="solid"
                      color="primary"
                      onClick={() => handleDeploy('staging')}
                      loading={deployMutation.isPending}
                    >
                      Deploy to Staging
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Production Environment */}
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography level="h4">Production Environment</Typography>
                  {deploymentData.current_deployments?.production && (
                    <Chip color={getStatusColor(deploymentData.current_deployments.production.status)}>
                      {deploymentData.current_deployments.production.status}
                    </Chip>
                  )}
                </Box>
                
                {deploymentData.current_deployments?.production ? (
                  <Box>
                    <Typography level="body-sm" mb={1}>
                      Version: {deploymentData.current_deployments.production.version}
                    </Typography>
                    <Typography level="body-sm" mb={2}>
                      Deployed: {new Date(deploymentData.current_deployments.production.created_at).toLocaleString()}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {deploymentData.deployment_url && (
                        <Button
                          size="sm"
                          variant="outlined"
                          color="primary"
                          startDecorator={<LaunchIcon />}
                          onClick={() => window.open(deploymentData.deployment_url, '_blank')}
                        >
                          View Live
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outlined"
                        color="warning"
                        onClick={() => handleRollback(deploymentData.current_deployments!.production!)}
                      >
                        Rollback
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Box textAlign="center" py={3}>
                    <Typography level="body-sm" color="neutral" mb={2}>
                      No production deployment
                    </Typography>
                    <Button
                      variant="solid"
                      color="success"
                      onClick={() => handleDeploy('production')}
                      loading={deployMutation.isPending}
                    >
                      Deploy to Production
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Quick Deploy */}
            <Card variant="soft" color="primary">
              <CardContent>
                <Typography level="h4" mb={2}>Quick Deploy</Typography>
                <Typography level="body-sm" mb={3}>
                  Deploy your latest changes with one click
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="solid"
                    startDecorator={<DeployIcon />}
                    onClick={() => navigate(`/cms/sites/${siteId}/deploy/wizard`)}
                  >
                    Guided Deploy
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleDeploy('staging')}
                    loading={deployMutation.isPending}
                  >
                    Quick Deploy to Staging
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Sidebar */}
        <Grid xs={12} md={4}>
          <Stack spacing={3}>
            {/* Pre-flight Checks */}
            <Card variant="outlined">
              <CardContent>
                <Typography level="h4" mb={2}>Pre-flight Checks</Typography>
                <List size="sm">
                  {deploymentData.preflight_checks?.map((check) => (
                    <ListItem key={check.id}>
                      <ListItemDecorator>
                        {getCheckIcon(check.status)}
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography level="body-sm">{check.name}</Typography>
                        {check.message && (
                          <Typography level="body-xs" color="neutral">
                            {check.message}
                          </Typography>
                        )}
                      </ListItemContent>
                    </ListItem>
                  )) || (
                    <ListItem>
                      <ListItemContent>
                        <Typography level="body-sm" color="neutral">
                          No checks configured
                        </Typography>
                      </ListItemContent>
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>

            {/* Deployment Status */}
            <Card variant="outlined">
              <CardContent>
                <Typography level="h4" mb={2}>Deployment Status</Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography level="body-sm" mb={1}>Site Status</Typography>
                    <Chip
                      size="sm"
                      color={deploymentData.site?.status === 'deployed' ? 'success' : 'warning'}
                    >
                      {deploymentData.site?.status || 'unknown'}
                    </Chip>
                  </Box>
                  {deploymentData.last_deployment && (
                    <Box>
                      <Typography level="body-sm" mb={1}>Last Deployment</Typography>
                      <Typography level="body-xs" color="neutral">
                        {new Date(deploymentData.last_deployment.created_at).toLocaleString()}
                      </Typography>
                      <Typography level="body-xs" color="neutral">
                        {deploymentData.last_deployment.environment} • v{deploymentData.last_deployment.version}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};